import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useCallback, useContext } from 'react';
import tracking from './index';

/**
 * Hook for tracking events in React components
 * Automatically includes currency from channel context
 *
 * @returns {Object} Tracking methods
 */
export function useTracking() {
  const { selectedChannel } = useContext(ChannelContext);
  // Get currency from channel
  const currency =
    selectedChannel?.defaultCurrency?.id ||
    selectedChannel?.currency?.id ||
    'SEK';

  // Wrap tracking methods to automatically include currency
  const trackViewItemList = useCallback(
    params => {
      tracking.viewItemList({
        currency,
        ...params
      });
    },
    [currency]
  );

  const trackSelectItem = useCallback(
    params => {
      tracking.selectItem({
        currency,
        ...params
      });
    },
    [currency]
  );

  const trackViewItem = useCallback(
    params => {
      tracking.viewItem({
        currency,
        ...params
      });
    },
    [currency]
  );

  const trackAddToCart = useCallback(
    params => {
      tracking.addToCart({
        currency,
        ...params
      });
    },
    [currency]
  );

  const trackRemoveFromCart = useCallback(
    params => {
      tracking.removeFromCart({
        currency,
        ...params
      });
    },
    [currency]
  );

  const trackViewCart = useCallback(params => {
    tracking.viewCart(params);
  }, []);

  const trackViewMiniCart = useCallback(params => {
    tracking.viewMiniCart(params);
  }, []);

  const trackBeginCheckout = useCallback(params => {
    tracking.beginCheckout(params);
  }, []);

  const trackPurchase = useCallback(params => {
    tracking.purchase(params);
  }, []);

  const trackViewPromotion = useCallback(params => {
    tracking.viewPromotion(params);
  }, []);

  const trackSelectPromotion = useCallback(params => {
    tracking.selectPromotion(params);
  }, []);

  const trackSearch = useCallback(params => {
    tracking.search(params);
  }, []);

  const trackPageView = useCallback(params => {
    tracking.pageView(params);
  }, []);

  const trackSearchResultClick = useCallback(params => {
    tracking.searchResultClick(params);
  }, []);

  const trackPopularCategoryClick = useCallback(params => {
    tracking.popularCategoryClick(params);
  }, []);

  const trackCustomEvent = useCallback((eventName, eventData) => {
    tracking.customEvent(eventName, eventData);
  }, []);

  return {
    // Ecommerce events
    trackViewItemList,
    trackSelectItem,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackViewMiniCart,
    trackBeginCheckout,
    trackPurchase,
    trackViewPromotion,
    trackSelectPromotion,

    // General events
    trackSearch,
    trackPageView,
    trackSearchResultClick,
    trackPopularCategoryClick,

    // Custom events
    trackCustomEvent,

    // Context data
    currency
  };
}
